import { api } from '../api';
import { state, isAdmin } from '../app';
import { esc, $, setHTML, formatCurrency, formatDateShort, timeAgo, greeting, icons, statusLabel } from '../helpers';
import { setBreadcrumb, skeletonStats } from '../ui';
import { renderRevenueChart } from '../chart-helper';

export async function renderDashboard() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Panel' }]);
  setHTML(content, skeletonStats(4));

  try {
    const data = await api.get('/dashboard');

    if (isAdmin()) {
      const projectStats: Record<string, number> = {};
      (data.stats.projects || []).forEach((p: any) => { projectStats[p.status] = p.count; });
      const totalProjects = Object.values(projectStats).reduce((a: number, b: number) => a + b, 0);
      const activeProjects = (projectStats.in_progress || 0) + (projectStats.review || 0);

      let activityHtml = '';
      if (!(data.recent_activity || []).length) {
        activityHtml = '<p style="color:var(--text-muted)">Henüz işlem yok</p>';
      } else {
        const iconFor: Record<string, string> = { login: 'log-in', login_failed: 'shield-alert', created: 'plus-circle', updated: 'edit', deleted: 'trash-2', approved: 'check-circle', rejected: 'x-circle', transfer_notified: 'wallet', password_changed: 'key', profile_updated: 'user' };
        data.recent_activity.slice(0, 10).forEach((a: any) => {
          const userName = esc(a.user_name || 'Sistem');
          const entity = a.entity_type ? ' <span style="color:var(--text-muted);font-size:0.8rem">(' + esc(statusLabel(a.entity_type)) + ')</span>' : '';
          activityHtml += `<div class="activity-item"><div class="activity-icon"><i data-lucide="${iconFor[a.action] || 'activity'}"></i></div><div class="activity-content"><div><strong>${userName}</strong> <span style="color:var(--text-secondary)">${esc(statusLabel(a.action)).toLowerCase()}</span>${entity}</div><div class="activity-time">${timeAgo(a.created_at)}</div></div></div>`;
        });
      }

      const alert = (color: string, icon: string, title: string, body: string, href: string) =>
        `<div class="card mb-2" style="border-left:3px solid ${color}"><div class="card-header" style="display:flex;align-items:center;justify-content:space-between"><h3><i data-lucide="${icon}" style="width:18px;height:18px;vertical-align:middle;margin-right:0.3rem;color:${color}"></i> ${title}</h3><a href="${href}" class="btn btn-primary btn-sm">İncele</a></div><div class="card-body"><p style="color:var(--text-muted);margin:0">${body}</p></div></div>`;

      setHTML(content, `<div>
        <div class="page-header"><h1>Panel</h1>
        <div class="page-header-actions">
          <a href="/clients" class="btn btn-secondary"><i data-lucide="user-plus"></i> Yeni Müşteri</a>
          <a href="/invoices" class="btn btn-primary"><i data-lucide="plus"></i> Yeni Fatura</a>
        </div></div>
        <div class="stats-grid">
          <div class="stat-card"><div class="stat-label"><i data-lucide="users"></i> Aktif Müşteri</div><div class="stat-value">${esc(data.stats.total_clients)}</div></div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="folder-kanban"></i> Devam Eden Proje</div><div class="stat-value">${esc(activeProjects)}</div><div class="stat-change">toplam ${esc(totalProjects)}</div></div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="banknote"></i> Bu Yıl Tahsilat (TL)</div><div class="stat-value">${formatCurrency(data.stats.total_revenue)}</div></div>
          <div class="stat-card"><div class="stat-label"><i data-lucide="clock"></i> Bekleyen Alacak (TL)</div><div class="stat-value">${formatCurrency(data.stats.pending_payments)}</div></div>
        </div>
        ${data.stats.pending_confirmations > 0 ? alert('var(--warning)', 'alert-circle', `${esc(data.stats.pending_confirmations)} havale bildirimi onay bekliyor`, 'Müşteriler ödeme yaptığını bildirdi. Banka hesabınızı kontrol edip onaylayın.', '/payments') : ''}
        ${data.stats.pending_approvals > 0 ? alert('var(--warning)', 'user-check', `Onay bekleyen kayıt (${esc(data.stats.pending_approvals)})`, 'Yeni müşteri kayıtları onayınızı bekliyor.', '/clients') : ''}
        ${data.stats.overdue_invoices > 0 ? alert('var(--danger)', 'alert-triangle', `${esc(data.stats.overdue_invoices)} faturanın vadesi geçti`, 'Vadesi geçmiş ödenmemiş faturalar var.', '/invoices') : ''}
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:1rem;align-items:start">
          <div class="card"><div class="card-header"><h3>Aylık Tahsilat</h3></div><div class="card-body" style="height:280px"><canvas id="revenue-chart"></canvas><div id="revenue-empty" style="display:none;text-align:center;color:var(--text-muted);padding:2rem 0;font-size:var(--text-sm)">Henüz tahsilat verisi yok</div></div></div>
          <div class="card"><div class="card-header"><h3>Son İşlemler</h3></div><div class="card-body" style="max-height:300px;overflow-y:auto">${activityHtml}</div></div>
        </div>
      </div>`);
      icons();
      if (data.stats.monthly_revenue?.length) {
        renderRevenueChart('revenue-chart', data.stats.monthly_revenue);
      } else {
        const canvas = document.getElementById('revenue-chart') as HTMLCanvasElement | null;
        const emptyMsg = document.getElementById('revenue-empty');
        if (canvas) canvas.style.display = 'none';
        if (emptyMsg) emptyMsg.style.display = 'block';
      }
    } else {
      // Müşteri paneli
      const pStats: Record<string, number> = {};
      (data.stats.projects || []).forEach((p: any) => { pStats[p.status] = p.count; });
      const tp = Object.values(pStats).reduce((a: number, b: number) => a + b, 0);
      const iStats: Record<string, number> = {};
      let totalOwed = 0;
      (data.stats.invoices || []).forEach((i: any) => {
        iStats[i.status] = i.count;
        if (i.status === 'unpaid' || i.status === 'overdue') totalOwed += i.total;
      });
      const unpaid = data.stats.unpaid_invoices || [];
      const unpaidCount = (iStats.unpaid || 0) + (iStats.overdue || 0);
      const hasOverdue = unpaid.some((u: any) => u.status === 'overdue' || (u.due_date && new Date(u.due_date) < new Date()));

      let urgentBanner = '';
      if (unpaid.length > 0) {
        let invoiceRows = '';
        unpaid.forEach((u: any, idx: number) => {
          const isOverdue = u.status === 'overdue' || (u.due_date && new Date(u.due_date) < new Date());
          const dueText = u.due_date ? (isOverdue ? '<span style="font-weight:600">Vadesi geçti</span>' : 'Vade ' + formatDateShort(u.due_date)) : '';
          invoiceRows += `<div style="display:flex;align-items:center;justify-content:space-between;padding:0.7rem 0;${idx < unpaid.length - 1 ? 'border-bottom:1px solid rgba(255,255,255,0.15);' : ''}">
            <div><span class="font-mono" style="font-weight:600">${esc(u.invoice_number)}</span><span style="opacity:0.8;margin-left:0.5rem;font-size:0.85rem">${dueText}</span></div>
            <div style="display:flex;align-items:center;gap:0.8rem"><span style="font-weight:700;font-size:1.05rem">${formatCurrency(u.total, u.currency)}</span>
              <a href="/invoices/${u.id}" class="btn" style="background:white;color:#1a1a1a;font-weight:600;padding:0.4rem 1rem;border-radius:8px;font-size:0.82rem;text-decoration:none">Ödeme Bilgileri</a></div>
          </div>`;
        });
        // Renk işlevsel: kırmızı "bir şey aksadı" demek, süsleme değil.
        const bannerColor = hasOverdue ? 'var(--danger)' : 'var(--accent)';
        urgentBanner = `<div style="background:${bannerColor};border-radius:var(--radius-md);padding:1.2rem 1.5rem;color:white;margin-bottom:1rem;box-shadow:var(--shadow-md)">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:0.6rem">
            <div style="display:flex;align-items:center;gap:0.5rem"><i data-lucide="${hasOverdue ? 'alert-circle' : 'file-text'}" style="width:20px;height:20px"></i>
              <span style="font-weight:700;font-size:1rem">${hasOverdue ? 'Vadesi geçmiş faturanız var' : unpaidCount + ' fatura ödeme bekliyor'}</span></div>
            <span style="font-size:1.3rem;font-weight:700">${formatCurrency(totalOwed, unpaid[0]?.currency)}</span>
          </div>${invoiceRows}</div>`;
      }

      // Sıfır değerli kart basmıyoruz: müşteri borcu yoksa "Ödenmemiş fatura 0"
      // görmesine gerek yok. Ödenmemiş fatura zaten yukarıdaki bantta duruyor,
      // ayrıca kart olarak tekrarlanmıyor.
      const openTickets = data.stats.open_tickets || 0;
      const cardList: string[] = [];
      if (tp > 0) {
        const running = pStats.in_progress || 0;
        cardList.push(`<div class="stat-card"><div class="stat-label"><i data-lucide="folder-kanban"></i> Projeleriniz</div><div class="stat-value">${esc(tp)}</div>${running ? `<div class="stat-change">${esc(running)} devam ediyor</div>` : ''}</div>`);
      }
      if (openTickets > 0) {
        cardList.push(`<div class="stat-card"><div class="stat-label"><i data-lucide="life-buoy"></i> Açık Destek Talebi</div><div class="stat-value">${esc(openTickets)}</div></div>`);
      }
      const cards = cardList.length ? `<div class="stats-grid">${cardList.join('')}</div>` : '';
      const allClear = !urgentBanner && !cardList.length;

      const firstName = esc(state.user!.name.split(' ')[0]);
      setHTML(content, `<div>
        <div class="page-header"><h1>${greeting()}, ${firstName}</h1></div>
        ${urgentBanner}
        ${cards}
        ${allClear ? '<div class="card"><div class="card-body"><p style="margin:0">Bekleyen bir işiniz yok. Ödenmemiş faturanız ve açık destek talebiniz bulunmuyor.</p></div></div>' : ''}
        <div class="card"><div class="card-header"><h3>Hızlı İşlemler</h3></div><div class="card-body" style="display:flex;gap:0.8rem;flex-wrap:wrap">
          ${unpaidCount > 0 ? '<a href="/invoices" class="btn btn-primary"><i data-lucide="wallet"></i> Faturalarım</a>' : ''}
          <a href="/projects" class="btn btn-secondary"><i data-lucide="folder-kanban"></i> Projeler</a>
          <a href="/tickets" class="btn btn-secondary"><i data-lucide="life-buoy"></i> Destek</a>
        </div></div>
      </div>`);
      icons();
    }
  } catch (err: any) {
    setHTML(content, `<div class="empty-state"><p>Panel yüklenemedi: ${esc(err.message)}</p></div>`);
  }
}
